import { Navigate, Route, Routes, useLocation } from 'react-router';
import Loading from '../common/lib/Loading';
import PageNotFound from '../common/lib/PageNotFound';
import useInitialized from '../common/lib/useInitialized';
import type { MultiLang } from '../config';
import PubmedPdfUtils from './lib/PubmedPdfUtils';
import PubmedPdfAuthorsByInitial from './PubmedPdfAuthorsByInitial';
import PubmedPdfIndex from './PubmedPdfIndex';
import PubmedPdfJournalsByYear from './PubmedPdfJournalsByYear';
import PubmedPdfPaperDesc from './PubmedPdfPaperDesc';
import PubmedPdfPapersByAuthor from './PubmedPdfPapersByAuthor';
import PubmedPdfPapersByJournal from './PubmedPdfPapersByJournal';
import PubmedPdfPapersByYear from './PubmedPdfPapersByYear';

interface Props {
    lang: MultiLang;
}

const useParams = () => new URLSearchParams(useLocation().search);

const AuthorPage = (props: Props) => {
    const { lang } = props;
    const params = useParams();
    const initial = params.get('initial');
    if (initial !== null && /^[A-Z]/.test(initial)) {
        return <PubmedPdfAuthorsByInitial lang={lang} initial={initial} />;
    }
    const authorIdStr = params.get('author');
    if (authorIdStr !== null && /^[0-9]+/.test(authorIdStr)) {
        const yearStr = params.get('year');
        const year = yearStr !== null && /^[0-9]+/.test(yearStr) ? parseInt(yearStr, 10) : 0;
        return <PubmedPdfPapersByAuthor lang={lang} authorId={parseInt(authorIdStr, 10)} year={year} />;
    }
    return <PageNotFound lang={lang} />;
};

const YearPage = (props: Props) => {
    const { lang } = props;
    const params = useParams();
    const yearStr = params.get('year');
    const journalIdStr = params.get('journal');
    if (yearStr === null || !/^[0-9]+/.test(yearStr) || (journalIdStr !== null && !/^[0-9]+/.test(journalIdStr))) {
        return <PageNotFound lang={lang} />;
    }
    const year = parseInt(yearStr, 10);
    const journalId = journalIdStr !== null ? parseInt(journalIdStr, 10) : 0;
    if (journalId === 0) {
        return <PubmedPdfJournalsByYear lang={lang} year={year} />;
    }
    return <PubmedPdfPapersByYear lang={lang} year={year} journalId={journalId} />;
};

const JournalPage = (props: Props) => {
    const { lang } = props;
    const params = useParams();
    const journalIdStr = params.get('journal');
    const yearStr = params.get('year');
    if (journalIdStr === null || !/^[0-9]+/.test(journalIdStr) || (yearStr !== null && !/^[0-9]+/.test(yearStr))) {
        return <PageNotFound lang={lang} />;
    }
    const year = yearStr !== null ? parseInt(yearStr, 10) : 0;
    return <PubmedPdfPapersByJournal lang={lang} journalId={parseInt(journalIdStr, 10)} year={year} />;
};

const PaperDescPage = (props: Props) => {
    const { lang } = props;
    const idStr = useParams().get('id');
    if (idStr === null || !/^[0-9]+/.test(idStr)) {
        return <PageNotFound lang={lang} />;
    }
    return <PubmedPdfPaperDesc lang={lang} id={parseInt(idStr, 10)} />;
};

const IndexRedirect = () => {
    const { search, hash } = useLocation();
    return <Navigate to={`${PubmedPdfUtils.getIndexUrl()}${search}${hash}`} replace />;
};

const PubmedPdf = (props: Props) => {
    const { lang } = props;
    const status = useInitialized(PubmedPdfUtils.initialize);
    if (status === null) {
        return <Loading />;
    }
    if (!status) {
        return <PageNotFound lang={lang} />;
    }
    return (
        <Routes>
            <Route index element={<IndexRedirect />} />
            <Route path="index.php" element={<PubmedPdfIndex lang={lang} />} />
            <Route path="author.php" element={<AuthorPage lang={lang} />} />
            <Route path="year.php" element={<YearPage lang={lang} />} />
            <Route path="journal.php" element={<JournalPage lang={lang} />} />
            <Route path="paper_desc.php" element={<PaperDescPage lang={lang} />} />
            <Route path="*" element={<PageNotFound lang={lang} />} />
        </Routes>
    );
};

export default PubmedPdf;
